import React, { useEffect, useRef, useState } from 'react';
import { cn } from '../../lib/utils';

// Full-screen ambient video (all devices) with navy overlay; static gradient fallback if it can't play.
const shouldPlayVideo = () => {
  if (typeof window === 'undefined') return false;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const conn = navigator.connection;
  const saveData = !!(conn && conn.saveData);
  return !reduced && !saveData;
};

const LoginBackdrop = () => {
  const [useVideo, setUseVideo] = useState(shouldPlayVideo);
  const [ready, setReady] = useState(false);
  const videoRef = useRef(null);

  // iOS/Android autoplay can be blocked (low-power mode, data saver). Kick play() explicitly,
  // and retry on the first touch/click or when the tab becomes visible again.
  useEffect(() => {
    if (!useVideo) return undefined;
    const v = videoRef.current;
    if (!v) return undefined;
    const tryPlay = () => {
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
    };
    tryPlay();
    const onVisible = () => { if (document.visibilityState === 'visible') tryPlay(); };
    const opts = { passive: true };
    window.addEventListener('touchstart', tryPlay, opts);
    window.addEventListener('pointerdown', tryPlay, opts);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('touchstart', tryPlay);
      window.removeEventListener('pointerdown', tryPlay);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [useVideo]);

  return (
    <div className="absolute inset-0 overflow-hidden bg-[#06122B]" aria-hidden="true" data-testid="login-backdrop">
      {/* Fallback / base gradient */}
      <div className="absolute inset-0 bg-[radial-gradient(120%_80%_at_20%_0%,#12306E_0%,#06122B_55%,#040B1C_100%)]" />
      <div className="absolute inset-0 opacity-[0.07] bg-[linear-gradient(rgba(255,255,255,.35)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.35)_1px,transparent_1px)] bg-[size:56px_56px]" />

      {useVideo && (
        <video
          ref={videoRef}
          className={cn('absolute inset-0 w-full h-full object-cover transition-opacity duration-[1400ms] ease-out', ready ? 'opacity-100' : 'opacity-0')}
          poster="/login-bg-poster.jpg"
          autoPlay
          muted
          loop
          playsInline
          webkit-playsinline="true"
          disablePictureInPicture
          disableRemotePlayback
          preload="auto"
          onPlaying={() => setReady(true)}
          onCanPlay={() => setReady(true)}
          onError={() => {
            // Fires per <source>; only fall back once no source could be loaded at all.
            setTimeout(() => { const v = videoRef.current; if (!v || v.error || v.networkState === 3) setUseVideo(false); }, 50);
          }}
          data-testid="login-bg-video"
        >
          {/* MP4 (H.264) first: universally supported on iOS Safari & Android; WebM as a smaller alternative */}
          <source src="/login-bg.mp4" type="video/mp4" />
          <source src="/login-bg.webm" type="video/webm" />
        </video>
      )}

      {/* Overlay: keeps the form legible while tinting the footage to brand navy/blue */}
      <div className="absolute inset-0 bg-[#06122B]/70 mix-blend-multiply" />
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(6,18,43,0.55)_0%,rgba(6,18,43,0.35)_45%,rgba(4,11,28,0.85)_100%)]" />
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[520px] rounded-full bg-[#3A6AFF]/25 blur-[140px]" />
    </div>
  );
};

export default LoginBackdrop;
